import React, { Fragment, useEffect, useCallback, useLayoutEffect, } from 'react';
import { format } from 'date-fns';
import Animated from 'react-native-reanimated';
import { View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { Spacer, AppView, CardView, Indicator, withAnimatedNavigationHeader, } from 'design-system';
import Text from 'design-system/Text';
import ListEmptyView from 'design-system/ListEmptyView';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { getValuationStatement, } from 'actions/invest';
import links from 'utils/links';
import useFetch from 'hooks/useFetch';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { selectActiveTradingAccountId } from 'features/invest/reducers/selectors';
import { isAndroid, isWeb } from '../../../constants';
const ValuationStatementScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const styles = useStyles(styleSet);
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const [result, , isFetching, fetch] = useFetch();
    useEffect(() => {
        if (tradingAccountId) {
            fetch(getValuationStatement(tradingAccountId));
        }
    }, [tradingAccountId]);
    const handleDownload = useCallback((statement) => async () => {
        if (isAndroid) {
            navigation.navigateDeprecated('Pdf', {
                url: statement.url,
                fileName: `Emma_valuation_statement_${format(statement.date, 'd_MMM_yyyy')}`,
            });
        }
        else {
            links.openLink(statement.url);
        }
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const Content = React.useMemo(() => {
        if (isFetching) {
            return (<View testID="loading-indicator">
              <Indicator dark style={styles.indicator}/>
            </View>);
        }
        if (result?.statements?.length) {
            return result.statements.map((statement) => (<Fragment key={String(statement.id)}>
              <TouchableBounce onPress={handleDownload(statement)}>
                <CardView withPadding noMarginTop noMarginBottom style={styles.card}>
                  <View style={styles.flex}>
                    <Text Numbers-16>
                      {format(statement.date, 'd MMM, yyyy')}
                    </Text>
                  </View>
                </CardView>
              </TouchableBounce>
              <Spacer.H16 />
            </Fragment>));
        }
        return (<ListEmptyView alt happy onCard title="No statements found" subTitle="There’s nothing to show right now."/>);
    }, [
        handleDownload,
        isFetching,
        result,
        styles.card,
        styles.flex,
        styles.indicator,
    ]);
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={paddingHorizontalStyle}>
            <ScreenInfoHeader title="Download the valuation statement" subTitle="This statement shows your portfolio value and performance over time"/>
            {Content}
          </Animated.ScrollView>
        </AppView>);
}, ({ colors }) => ({
    hideTitleOnScroll: true,
    title: 'Valuation statement',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    card: {
        borderWidth: 1,
        flexDirection: 'row',
        borderColor: colors.cards.onDark,
    },
    flex: {
        flex: 1,
    },
    indicator: {
        flex: undefined,
        marginTop: '50%',
    },
}));
export default ValuationStatementScreen;
